import { useQuery } from "@tanstack/react-query";
import api from "../../../../../services/api";

interface Connector {
  name: string;
  is_container_started: boolean;
  is_service_started: boolean;
}

// The endpoint shells out to `connectors_manage list`, and connector configuration barely ever
// changes, so keep the client from re-asking on every report-summary visit.
const STALE_TIME = 5 * 60 * 1000;

/**
 * Whether any connector - EDR or ServiceNow - is enabled on the appliance. The endpoint synthesizes
 * the ServiceNow entry as `is_container_started: true` + `is_service_started: <servicenow.enable>`,
 * so this single predicate covers both kinds with no special-casing.
 *
 * Returns `undefined` while the answer is unknown: still loading, or the request failed (the view
 * returns 400 on any internal error). Callers must treat `undefined` as "maybe" and fall back -
 * never as "no connectors".
 */
const useConnectorsEnabled = (): boolean | undefined => {
  const { data } = useQuery({
    queryKey: ["connectorsList"],
    queryFn: () => api.listConnectors(),
    retry: false,
    staleTime: STALE_TIME,
    select: response =>
      Array.isArray(response.data)
        ? (response.data as Connector[]).some(
            connector =>
              connector.is_container_started && connector.is_service_started
          )
        : undefined
  });

  return data;
};

export default useConnectorsEnabled;
